import React, { useEffect, useState } from "react";
import { getPricing } from "../../Forms/Misc/utilities";
import { extractNumbers } from "../utilities";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

const DoorSpecifications = (props) => {
  const [productIDs, setProductIDs] = useState([]);
  const [pricing, setPricing] = useState(0);
  const [margin, setMargin] = useState(props.data.margin);
  const [marginDialog, setDialog] = useState(false);

  useEffect(() => {
    const extractedNumbers = extractNumbers(props.data);
    setProductIDs((prevProductIDs) => [...prevProductIDs, ...extractedNumbers]);

    // Clean up function (runs on component unmount)
    return () => {
      setProductIDs([]);
    };
  }, [props.data]);

  const fetchStockPrice = async (setter, productCode) => {
    try {
      const response = await fetch(
        `/api/doors/kits/getStockItemPrice?productCode=${productCode}`
      );
      const data = await response.json();

      if (response.ok) {
        const price = parseFloat(data.price).toFixed(2); // String with 2 decimal places
        await setter(parseFloat(price)); // Convert back to number
      } else {
        console.error("Error fetching price:", data.error);
        await setter(0.0); // Set a default number value
      }
    } catch (error) {
      console.error("Error during API request:", error);
      await setter(0.0); // Set a default number value
    }
  };

  useEffect(() => {
    // No price until the door is fully configured — a partial selection
    // would quote labor + adders on an empty product list.
    if (!props.formValid) {
      setPricing(0);
      return;
    }

    const fetchPrice = async () => {
      if (props.data.core === "Bifold" && props.data.width) {
        let width = props.data.width;
        const match = width.match(/\((\d+)\)/);
        const productCode = match ? parseInt(match[1], 10) : null;

        if (productCode) {
          await fetchStockPrice(setPricing, productCode); // Ensuring it's awaited
        }
      } else {
        if (props.data.isDouble) {
          getPricing(
            productIDs,
            1,
            props.data.size_jamb_type,
            setPricing,
            props.data.ftwayne_prep
          );
        } else {
          getPricing(
            productIDs,
            0,
            props.data.size_jamb_type,
            setPricing,
            props.data.ftwayne_prep
          );
        }
      }
    };

    fetchPrice(); // Call the async function properly
  }, [productIDs, props.formValid]);

  useEffect(() => {
    // Check if the core type has changed
    if (props.data.core && props.data.core !== "Bifold") {
      // Set slab_style and width to null when the core type changes
      props.data.slab_style = null;
      props.data.width = null;
    }
  }, [props.data.core]);

  useEffect(() => {
    if (props.data.door_type === "double") {
      // Set slab_style and width to null when the core type changes
      props.data.handing = "Double";
    } else if (props.data.size_jamb_type?.includes("Door Slab Only")) {
      props.data.handing = "N/A";
    } else if (props.data.door_type === "bifold") {
      props.data.handing = "Bifold";
      props.data.core = "Bifold";
    }
  }, [props.data]);

  useEffect(() => {
    // Pricing is on the original Door Builder page, this pricing is only used to send pricing back to Door Builder to add to the door table when added to quote?
    if (props.data.core === "Bifold") {
      props.setEstimatedPrice(parseFloat(pricing * props.data.qty).toFixed(2));
    } else {
      props.setEstimatedPrice(
        Math.round(
          (pricing * props.data.qty * (100 / (100 - parseFloat(margin))) +
            Number.EPSILON) *
            100
        ) / 100
      );
    }
  }, [pricing, props.data.qty, margin, props.data.margin]);

  useEffect(() => {
    // Pricing is on the original Door Builder page, this pricing is only used to send pricing back to Door Builder to add to the door table when added to quote?
    setMargin(props.data.margin);
  }, [props.marginFlag]);

  // Friendly labels for spec keys
  const labelMap = {
    casing_type: "Casing",
    hinge_finish: "Hinge Finish",
    size_jamb_type: "Jamb",
    stop: "Stop",
    handing: "Handing",
    door_type: "Door Type",
    stop_applied: "Stop Applied",
    applied: "Casing Applied",
    bolt_type: "Bolt Type",
    backset: "Backset",
    isDouble: "Double Door",
    height: "Height",
    qty: "Quantity",
    ftwayne_prep: "Ft. Wayne Prep",
    catch_astragal: "Catch / Astragal",
    special_instructions: "Instructions",
  };

  const hiddenKeys = new Set([
    "core",
    "slab_style",
    "width",
    "margin",
    "estimated_price",
  ]);

  const formatValue = (key, value) => {
    if (key === "isDouble") return value ? "Yes" : "No";
    if (key === "ftwayne_prep") return value ? "Yes" : "No";
    if (typeof value === "string") {
      // Strip the "(productId)" suffix for display
      return value.replace(/\(\d+\)$/, "").replace(/\(null\)$/, "").trim();
    }
    return value;
  };

  return (
    <>
      <Dialog
        open={marginDialog}
        onClose={() => setDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Adjust Margin (%)</DialogTitle>
        <DialogContent>
          <input
            type="text"
            name="margin"
            aria-label="Adjust Margin (%)"
            className="app-input w-full rounded-xl border px-3 py-2.5 text-sm"
            value={margin}
            onChange={(e) => setMargin(e.target.value)}
            max={99}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^0-9.]/g, "");
              const parts = e.target.value.split(".");
              if (parts.length > 2) { parts.pop(); e.target.value = parts.join("."); }
              const integerPart = parts[0];
              if (integerPart.length > 2) {
                e.target.value = integerPart.substring(0, 2) + (parts[1] ? "." + parts[1] : "");
              }
              if (parts[1] && parts[1].length > 2) {
                e.target.value = integerPart + "." + parts[1].substring(0, 2);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="app-btn app-btn-primary"
            onClick={() => setDialog(false)}
          >
            Done
          </button>
        </DialogActions>
      </Dialog>

      <h2 className="app-eyebrow mb-3 text-center">
        Specifications
      </h2>

      <div className="app-card overflow-hidden rounded-xl border">
        {/* Price banner — no price until every required field is selected */}
        {props.formValid ? (
          <button
            type="button"
            className="w-full border-b app-divider bg-emerald-500/10 px-4 py-3 transition hover:bg-emerald-500/15"
            onClick={() => setDialog(true)}
          >
            {props.data.core && props.data.slab_style && props.data.width && (
              <p className="app-muted mb-0.5 text-xs">
                {props.data.core} {props.data.slab_style} {props.data.width?.replace(/\(\d+\)$/, "").replace(/ - $/, "").trim()}
              </p>
            )}
            <div className="flex items-baseline justify-center gap-1.5">
              <span className="text-sm font-medium">Est. Price:</span>
              <span className="text-xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                ${props.estimatedPrice || "0.00"}
              </span>
            </div>
            <p className="app-muted mt-0.5 text-[11px]">Tax not included &middot; Click to adjust margin</p>
          </button>
        ) : (
          <div className="w-full border-b app-divider px-4 py-3 text-center">
            <div className="flex items-baseline justify-center gap-1.5">
              <span className="app-muted text-sm font-medium">Est. Price:</span>
              <span className="app-muted text-xl font-bold">—</span>
            </div>
            <p className="app-muted mt-0.5 text-[11px]">
              Complete all steps to see pricing
            </p>
          </div>
        )}

        {/* Spec rows */}
        <div className="max-h-64 divide-y divide-[color:var(--app-divider)] overflow-y-auto">
          {Object.keys(props.data).map((key) => {
            if (hiddenKeys.has(key) || props.data[key] === null || props.data[key] === undefined) {
              return null;
            }
            const val = formatValue(key, props.data[key]);
            if (val === "" || val === undefined) return null;
            return (
              <div className="flex text-xs" key={key}>
                <div className="app-muted w-2/5 truncate px-3 py-2 font-medium">
                  {labelMap[key] || key.replace(/_/g, " ").toUpperCase()}
                </div>
                <div className="w-3/5 truncate px-3 py-2 font-medium">
                  {val}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default DoorSpecifications;
